import { useCallback, useEffect, useRef, useState } from 'react';
import { detectWebGL2, normalizeManifest } from '../../lib/visitManifest';
import { formatBytes } from './site3dModel';
import { linkCameras } from './scanLink';
import { KIND_LABEL } from './siteTimelineModel';
import StageFrame from './SiteStage';
import { STAGE } from './siteUi';

// Same renderer as Virtual Visit (Spark on three.js), loaded only when a scan opens.
const loadEngine = () => import('../visit/splatEngine').then((m) => m.createSplatEngine);

function loadingLine(st) {
  if (st.frac > 0) return `Loading ${Math.min(100, Math.floor(st.frac * 100))}%`;
  return st.loaded > 0 ? `Loading ${formatBytes(st.loaded)}` : 'Loading scan';
}

// items: [{ url, format, date? }] — one walk-through scan, or two dates with one shared camera.
export default function ScanView({ items, onClose, title, engineLoader = loadEngine, hasWebGL2 = detectWebGL2 }) {
  const paneRefs = useRef([]);
  const engines = useRef([]);
  const link = useRef(null);
  const shown = useRef(null);
  const applyShown = () => engines.current.forEach((e, i) => e && e.setPaused && e.setPaused(Boolean(shown.current && shown.current[i] === false)));
  const onShown = useCallback((flags) => { shown.current = flags; applyShown(); }, []);
  const [state, setState] = useState(() => items.map(() => ({ status: 'loading', frac: 0, loaded: 0 })));
  const key = items.map((i) => i.url).join('|');

  useEffect(() => {
    let cancelled = false;
    const patch = (i, next) => setState((s) => s.map((x, j) => (j === i ? { ...x, ...next } : x)));
    setState(items.map(() => ({ status: 'loading', frac: 0, loaded: 0 })));
    if (!hasWebGL2()) {
      setState(items.map(() => ({ status: 'error', message: '3D scans need a newer browser.' })));
      return undefined;
    }
    const ready = items.map(() => false);
    const relink = () => {
      if (cancelled || items.length < 2 || !ready.every(Boolean)) return;
      if (link.current) link.current.unlink();
      link.current = linkCameras(engines.current);
      link.current.sync(0);
    };
    engineLoader()
      .then((create) => Promise.all(items.map((it, i) => {
        const manifest = normalizeManifest({ id: `scan-${i}`, url: it.url, format: it.format, title: 'Site scan' });
        return create(paneRefs.current[i], manifest, {
          ariaLabel: '3D scan. Drag to orbit, pinch or scroll to zoom.',
          clearColor: STAGE.clear,
          onProgress: (_id, frac, loaded) => patch(i, { frac: frac || 0, loaded: loaded || 0 }),
          onReady: () => { ready[i] = true; patch(i, { status: 'ready' }); relink(); },
          onError: () => patch(i, { status: 'error', message: "Couldn't open this scan here." }),
          onRoomError: () => patch(i, { status: 'error', message: "Couldn't open this scan here." }),
        });
      })))
      .then((list) => {
        if (cancelled) list.forEach((e) => e && e.dispose());
        else { engines.current = list; applyShown(); relink(); }
      })
      .catch(() => {
        if (!cancelled) setState(items.map(() => ({ status: 'error', message: "3D scans aren't available in this browser." })));
      });
    return () => {
      cancelled = true;
      if (link.current) link.current.unlink();
      link.current = null;
      engines.current.forEach((e) => e && e.dispose());
      engines.current = [];
    };
  }, [key]);

  const reset = (i = 0) => {
    const e = engines.current[i] || engines.current[0];
    if (!e) return;
    if (link.current) link.current.lead(engines.current[i] ? i : 0);
    e.resetView();
  };

  const pair = items.length > 1;
  return (
    <StageFrame testid="site-scan" paneTestid="site-scan-pane-" title={title || 'Site scan'}
      items={items.map((it) => ({ ...it, kindLabel: KIND_LABEL.site_scan }))}
      state={state.map((st) => ({ ...st, progress: loadingLine(st) }))}
      paneRefs={paneRefs} onReset={reset} onClose={onClose} onShown={onShown}
      hint={`${pair ? 'Both dates move together. ' : ''}Drag to orbit. Pinch or scroll to zoom.`} />
  );
}
